import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import StyleSheet from 'styles'
import { UserSelectionActions } from 'actionsets'
import FilterListIcon from '@material-ui/icons/FilterList'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List';
import TextField from '@material-ui/core/TextField'
import Tooltip from '@material-ui/core/Tooltip'
import { Pagination, UserListItem, SelectionCounter, ConditionalRender, ConfirmationDialog, LinkButton } from 'components'
import Checkbox from 'components/styled/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel';
import UserSelectFilters from './UserSelectFilters'
import { debounce } from 'utils'
import InputAdornment from '@material-ui/core/InputAdornment';

import { SectionLoader } from 'components'

export class UserSelect extends Component{

  static defaultProps = {
    prefilters: {},
    selection: []
  }

  constructor(props){
    super(props)
    UserSelectionActions.bindActions(this)

    const selection = {}
    props.selection.forEach(id => selection[id] = true)

    this.state = {
      filtersOpen: false,
      showSelection: false,
      filters: {},
      page: 1,
      selection
    }

    this.filterSubmitRef = null;
  }

  componentWillUnmount(){
    this.actions.close()
  }

  componentDidMount(){
    return this.query()
  }

  dependenciesMet(){
    return this.props.requests.length === 0
  }

  query(){
    const filter = {...this.props.prefilters, ...this.filters}
    return this.actions.index(this.currentPage, filter)
  }

  get searchPattern() {
    return this.state.filters.name || ''
  }

  set currentPage(page){
    this.setState({page}, this.query)
  }

  get currentPage(){
    return Math.min(this.state.page, this.totalPages)
  }

  get totalPages(){
    return this.props.totalPages
  }

  get selectedUserCount(){
    return Object.values(this.state.selection).filter(x => x).length
  }

  get selectedUserIds(){
    return Object.entries(this.state.selection).filter(([id, selected]) => selected).map(([id, selected]) => id)
  }

  get hasSelection(){
    return !!this.selectedUserCount
  }

  get hasAllSelected(){
    return this.props.currentPage.every(({id}) => this.state.selection[id])
  }

  get hasAllPagesSelected(){
    return this.props.matchingIds.every((id) => this.state.selection[id])
  }

  get filters(){
    return this.state.showSelection ? {ids: this.selectedUserIds} : this.state.filters
  }

  queryLater = debounce(() => {
    this.query()
  }, 300)

  handleFilterChanged = filters => {
    this.setState({filters}, this.query)
  }

  handleNameFilterChanged = (event) => {
    const searchPattern = event.target.value
    const existingFilters = this.state.filters

    this.setState({filters: {...existingFilters, name: searchPattern}})
    this.queryLater()
  }

  handleClearSelection = () => {
    this.setState({selection: {}})
  }

  handleSelect = () => {
    this.actions.select(this.selectedUserIds)
  }

  handleOpenFilters = () => {
    this.setState({filtersOpen: true})
  }

  handleCloseFilters = () => {
    this.setState({filtersOpen: false})
  }

  handleToggleSelect = (id, selected) => {
    return () => {
      this.setState({selection: {...this.state.selection, [id]: !selected}})
    }
  }

  handleToggleSelectAll = () => {
    const selection = {...this.state.selection}
    if(this.hasAllSelected){
      this.props.currentPage.forEach(({id}) => selection[id] = false)
    }else{
      this.props.currentPage.forEach(({id}) => selection[id] = true)
    }
    this.setState({selection})
  }

  handleToggleSelectAllPages = (e) => {
    const selection = {...this.state.selection}
    if(this.hasAllPagesSelected){
      this.props.matchingIds.forEach((id) => selection[id] = false)
    }else{
      this.props.matchingIds.forEach((id) => selection[id] = true)
    }
    this.setState({selection})
    e.preventDefault()
  }

  renderListItem = (user) => {
    const { id } = user
    return(
      <UserListItem user={ user } key={ id }
        showAvatar={false}
        selected={ !!this.state.selection[id] }
        onSelected={ this.handleToggleSelect(id, this.state.selection[id]) }/>
    )
  }

  renderEntireSelectAllLink = () =>
    this.props.totalPages > 1 ?
      <LinkButton className={styles.selectAllLinkAllPages} onClick={this.handleToggleSelectAllPages}>
        {`${this.hasAllPagesSelected ? '| Deselect' : '| Select'} across all pages (${this.props.matchingIds.length} users)`}
      </LinkButton> : false

  renderFilters = () => {
    return (
      <UserSelectFilters
        filters={this.state.filters}
        prefilters={this.props.prefilters}
        onFilterChanged={this.handleFilterChanged}
        onRequestClose={this.handleCloseFilters}
        submitRef={(filterSubmitRef) => this.filterSubmitRef = filterSubmitRef}
      />
    )
  }

  renderForm = () => {
    return (
      <div className={styles.root}>
        <SelectionCounter selection={this.state.selection} onClear={this.handleClearSelection} selectionType="investors"/>
        <TextField className={styles.fixed} style={{marginTop: '0px'}} fullWidth value={this.searchPattern} margin="normal" label="Search" onChange={this.handleNameFilterChanged} InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <Tooltip title='Advance filtering'>
                <IconButton onClick={this.handleOpenFilters}>
                  <FilterListIcon/>
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )
        }}/>
        <FormControlLabel
          className={styles.fixed}
          style={{marginLeft: '-16px'}}
          label={
            this.hasAllSelected ?
              <span className={styles.selectAllLabel}>Deselect All {this.renderEntireSelectAllLink()}</span> :
              <span className={styles.selectAllLabel}>Select All</span>
            } control={<Checkbox small theme='dark' checked={this.hasAllSelected} onChange={this.handleToggleSelectAll}/>}/>
        <List className={styles.list}>
         { this.props.currentPage.map(this.renderListItem) }
        </List>
        <Pagination theme={'dark'} className={`${styles.pagination} ${styles.fixed}`} totalPages={this.totalPages} page={this.currentPage} onPageSelected={(page) => this.currentPage = page} style={{}} linkStyle={{}}/>
      </div>
    )
  }

  get cancelLabel() {
    if(this.state.filtersOpen) {
      return 'Back'
    }

    return this.props.cancelActionName || 'Cancel'
  }

  get confirmLabel() {
    if(this.state.filtersOpen) {
      return 'Apply'
    }

    return this.props.actionName || 'Select'
  }

  get canConfirm() {
    if(this.state.filtersOpen) {
      return true
    }

    return this.hasSelection
  }

  handleCancel = () => {
    if(this.state.filtersOpen) {
      this.handleCloseFilters()
    } else {
      this.actions.cancel()
    }
  }

  handleConfirm = () => {
    if(this.state.filtersOpen) {
      if(this.filterSubmitRef) {
        this.filterSubmitRef()
      }
    } else {
      this.handleSelect()
    }
  }

  render = () => {
    const title = this.props.title || "Select Users"

    return (
      <ConfirmationDialog title={title} open={true}
              className={styles.dialog}
              onCancel={this.handleCancel}
              canConfirm={this.canConfirm}
              onConfirm={this.handleConfirm}
              cancelLabel={this.cancelLabel} confirmLabel={this.confirmLabel}>
        <Fragment>
          {
            !this.dependenciesMet() &&
            <SectionLoader />
          }
          { this.state.filtersOpen &&
            this.renderFilters()
          }
          { !this.state.filtersOpen &&
            this.renderForm()
          }
        </Fragment>
      </ConfirmationDialog>
    )
  }
}

const styles = StyleSheet({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flex: 1
  },
  fixed: {
    flexShrink: 0,
    flexGrow: 0
  },
  pagination: {
    padding: 0
  },
  currentSelection: {
    height: 0,
    transition: 'all 200ms',
    marginBottom: 10,
    overflow: 'hidden'
  },
  currentSelectionActive: {
    height: 40,
  },
  currentSelectionBackground: {
    background: '#d88212',
    border: '1px solid #933e00',
    padding: 10,
    opacity: 0,
    transition: 'all 1000ms',
    display: 'flex'
  },
  currentSelectionBackgroundActive: {
    opacity: 1.0
  },
  currentSelectionText: {
    flex: 1
  },
  currentSelectionButton: {
    width: 24,
    height: 24,
    marginTop: -3
  },
  cardActions: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'flex-end'
  },
  cardContent: {
    paddingTop: 0
  },
  title: {
    flex: 1,
    color: 'white'
  },
  selectAllLabel: {
    paddingLeft: '16px'
  },
  selectAllLinkAllPages: {
    fontWeight: 'bold',
    textDecoration: 'underline'
  },
  formActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'end',
    background: 'rgba(255, 255, 255, 0.2)',
    padding: 10
  },
  list: {
    height: '100px',
    marginLeft: '-16px',
    marginTop: '16px',
    paddingTop: '0',
    paddingBottom: '0',
    maxWidth: '90vw',

    flex: '1 1 auto',
    overflowY: 'auto',
    // '&::-webkit-scrollbar': {
    //   width: '5px'
    // },
    // '&::-webkit-scrollbar-track-piece': {
    //   backgroundColor: '#1B3044'
    // },
    // '&::-webkit-scrollbar-thumb': {
    //   borderRadius: '2.5px',
    //   backgroundColor: '#5F5F5F'
    // }
  },
  dialog: {
    height: 770
  }
}, { classNamePrefix: 'UserSelect-' })

export default connect(({userSelect}) => userSelect)(({open, ...rest}) =>
  open ? <ConditionalRender component={UserSelect} {...rest} /> : false
)